import React, { useEffect, useRef } from "react";

// Decorative, interactive background for the "Sound familiar?" section:
// a drifting cloud of CPF "alphabet soup" tokens that scatter away from the
// pointer, plus a soft gold glow following it. Tokens tied to a self-check
// question (q) turn teal when answered "Yes" and gold when "Not sure".
//
// Pointer-events are disabled so it never interferes with the content.
// Respects prefers-reduced-motion (no drift / repulsion) and skips pointer
// tracking on touch devices.

const TOKENS = [
  // left margin
  { t: "55", x: 5, y: 12, s: 1.6, q: 0, d: 11 },
  { t: "BRS", x: 3, y: 26, s: 1.1, q: 1, d: 13 },
  { t: "$5,000", x: 9, y: 38, s: 1.0, q: 0, d: 12 },
  { t: "OA", x: 12, y: 50, s: 0.9, q: 0, d: 12 },
  { t: "30\u201340 yrs", x: 4, y: 63, s: 1.1, q: 3, d: 12 },
  { t: "65", x: 10, y: 76, s: 1.4, q: null, d: 11 },
  { t: "?", x: 6, y: 90, s: 1.3, q: null, d: 12 },
  // right margin
  { t: "?", x: 91, y: 6, s: 2.0, q: null, d: 10 },
  { t: "CPF LIFE", x: 84, y: 18, s: 1.15, q: 2, d: 14 },
  { t: "Basic", x: 88, y: 31, s: 0.95, q: 2, d: 11 },
  { t: "payouts", x: 85, y: 44, s: 0.95, q: 3, d: 14 },
  { t: "RA", x: 93, y: 57, s: 1.2, q: 0, d: 9 },
  { t: "SA", x: 87, y: 70, s: 0.9, q: null, d: 11 },
  { t: "$", x: 90, y: 83, s: 1.7, q: 3, d: 13 },
  { t: "70", x: 95, y: 94, s: 1.2, q: null, d: 10 },
  // top band (inside the section padding, above the content)
  { band: true, t: "FRS", x: 30, y: 2, s: 1.25, q: 1, d: 9 },
  { band: true, t: "ERS", x: 52, y: 1, s: 1.05, q: 1, d: 12 },
  { band: true, t: "Standard", x: 70, y: 3, s: 0.9, q: 2, d: 13 },
  // bottom band
  { band: true, t: "Escalating", x: 40, y: 96, s: 0.9, q: 2, d: 10 },
  { band: true, t: "?", x: 62, y: 95, s: 1.5, q: null, d: 15 },
];

const RADIUS = 220; // px, pointer influence
const PUSH = 70; // px, max displacement

export const QuestionCloud = ({ answers = {} }) => {
  const layerRef = useRef(null);
  const tokenRefs = useRef([]);
  const pointer = useRef({ x: -9999, y: -9999, active: false });
  const frame = useRef(0);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return undefined;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (reduce || !fine) return undefined;

    const section = layer.parentElement;
    const cur = tokenRefs.current.map(() => ({ x: 0, y: 0 }));

    const tick = () => {
      const p = pointer.current;
      tokenRefs.current.forEach((el, i) => {
        if (!el) return;
        let tx = 0;
        let ty = 0;
        if (p.active) {
          const r = el.getBoundingClientRect();
          const cx = r.left + r.width / 2;
          const cy = r.top + r.height / 2;
          const dx = cx - p.x;
          const dy = cy - p.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < RADIUS) {
            const f = (1 - dist / RADIUS) ** 2 * PUSH;
            tx = (dx / dist) * f;
            ty = (dy / dist) * f;
          }
        }
        // ease towards target
        cur[i].x += (tx - cur[i].x) * 0.12;
        cur[i].y += (ty - cur[i].y) * 0.12;
        el.style.setProperty("--rx", `${cur[i].x.toFixed(2)}px`);
        el.style.setProperty("--ry", `${cur[i].y.toFixed(2)}px`);
      });
      frame.current = requestAnimationFrame(tick);
    };

    const onMove = (e) => {
      pointer.current = { x: e.clientX, y: e.clientY, active: true };
      const b = section.getBoundingClientRect();
      layer.style.setProperty("--gx", `${(((e.clientX - b.left) / b.width) * 100).toFixed(2)}%`);
      layer.style.setProperty("--gy", `${(((e.clientY - b.top) / b.height) * 100).toFixed(2)}%`);
      layer.classList.add("is-live");
    };
    const onLeave = () => {
      pointer.current.active = false;
      layer.classList.remove("is-live");
    };

    section.addEventListener("pointermove", onMove);
    section.addEventListener("pointerleave", onLeave);
    frame.current = requestAnimationFrame(tick);
    return () => {
      section.removeEventListener("pointermove", onMove);
      section.removeEventListener("pointerleave", onLeave);
      cancelAnimationFrame(frame.current);
    };
  }, []);

  return (
    <div ref={layerRef} className="vid-cloud" aria-hidden="true" data-testid="video-question-cloud">
      <span className="vid-cloud__glow" />
      {TOKENS.map((tok, i) => {
        const state = tok.q !== null && tok.q !== undefined ? answers[tok.q] : undefined;
        return (
          <span
            key={`${tok.t}-${i}`}
            className={`vid-token-wrap${tok.band ? " vid-token-wrap--band" : ""}`}
            style={{ "--x": `${tok.x}%`, "--y": `${tok.y}%`, "--d": `${tok.d}s`, "--i": i }}
          >
            <span
              ref={(el) => (tokenRefs.current[i] = el)}
              className={`vid-token${tok.t === "?" ? " vid-token--q" : ""}${state ? ` is-${state}` : ""}`}
              style={{ "--s": tok.s }}
            >
              {tok.t}
            </span>
          </span>
        );
      })}
    </div>
  );
};

export default QuestionCloud;
